import {Subject, merge} from 'rxjs';
import {AUTOSAVE_FAILURE} from 'common/types';
import _ from 'lodash';
import DynamicComponentActions from 'go/actions/dynamicComponent';
import REST from 'go/requests/rest';
import {debounceTime, concatMap, filter} from 'rxjs/operators';
import {getInfo} from 'common/storeUtils';
import goStore from 'go/store';
import autoSave from 'go/actions/api/autoSave';
import {ENDPOINT_DYNAMIC_DATA} from 'common/endpoints';
import {BREAKPOINTS, debugBreakpoint} from 'common/debug';

export const SAVE_ENTITY_NETWORK_ERROR_MSG =
  'You have encountered an unexpected error. Please refresh your browser and try again. If you continue to receive this message, please contact the help desk at <a href="mailto:femago@fema.dhs.gov">femago@fema.dhs.gov</a> or 1-877-611-4700.';
export const SAVE_ENTITY_NETWORK_ERROR_TITLE = 'Unexpected error';

class ConcurrentEditProcessor {
  entityTypeMap = new Map();
  entityQueue = new Set();
  pendingQueue = new Set();
  observables = [];

  constructor() {
    this.entityTypeMap.set('data', new Subject());
    this.entityTypeMap.set('subApplication', new Subject());
    this.entityTypeMap.set('application', new Subject());
    this.entityTypeMap.set('ehpData', new Subject());
    this.entityTypeMap.set('ehpRecommendationData', new Subject());
    this.entityTypeMap.set('awardRecommendation', new Subject());
    this.entityTypeMap.set('payment', new Subject());
    this.entityTypeMap.set('amendment', new Subject());
    this.entityTypeMap.set('grant', new Subject());
    this.entityTypeMap.set('subGrant', new Subject());
    this.entityTypeMap.set('subAwardRecommendation', new Subject());
    this.entityTypeMap.set('subAmendmentRecommendation', new Subject());
    this.entityTypeMap.set('subCloseoutReport', new Subject());
    this.entityTypeMap.set('subCloseoutRecommendation', new Subject());
    this.entityTypeMap.set('financialReport', new Subject());
    this.entityTypeMap.set('bsirReport', new Subject());
    this.entityTypeMap.set('performanceProgressReport', new Subject());
    this.entityTypeMap.set('rfi', new Subject());
    this.entityTypeMap.set('closeoutRecommendation', new Subject());
    this.entityTypeMap.set('closeoutDeobligationApproval', new Subject());
    this.entityTypeMap.set('closeoutReport', new Subject());
    this.registerObservables();
  }

  registerObservables() {
    this.entityTypeMap.forEach((value, key) => {
      const observable$ = value.asObservable();
      const pipeObservable$ = observable$.pipe(
        filter((request) => !this.pendingQueue.has(request)),
        debounceTime(1000),
        concatMap((request) => this.processPostRequest(request))
      );
      this.observables.push(pipeObservable$);
    });
    merge(...this.observables).subscribe((results) => {
      // Required empty subscription block
      // Subscribe to observables to process,
      // even though process complete in concatMap operator level.
    });
  }

  processPostRequest(request) {
    if (this.entityQueue.has(request.entityId)) {
      this.pendingQueue.add(request);
    } else {
      this.entityQueue.add(request.entityId);
      return new Promise((resolve, reject) => {
        const state = goStore.store.getState();
        const token = _.get(state, 'auth.token');
        if (request.from === 'dynamicSaveSaga') {
          const apiObject = state.dynamicComponent.serviceCall;
          const payload = {data: state.dynamicComponent.data};
          apiObject.apiCall(payload, token, apiObject.args).then((response) => {
            this.dispatchEvents(request, response, resolve);
          });
        } else {
          let entityData = request.entityTypeReduxPath
            ? _.get(state, `dynamicComponent.${request.entityTypeReduxPath}`)
            : request.entityType !== 'data'
            ? _.get(state, ['dynamicComponent', 'context', request.entityType])
            : _.get(state, ['dynamicComponent', 'data']);
          const endpoint = `${ENDPOINT_DYNAMIC_DATA}entity/${_.get(
            entityData,
            'entityId'
          )}`;

          const method = request.patchRequest ? 'PATCH' : 'PUT';

          goStore.store
            .dispatch(
              autoSave.save(token, endpoint, method, entityData, request.entityType)
            )
            .then((payload) => {
              if (payload.error && payload.payload.status !== 409) {
                return Promise.reject(payload.payload.message);
              } else {
                this.dispatchEvents(request, payload, resolve);
              }
            })
            .catch((err) => {
              this.handleError(request, resolve, err);
            });
        }
      });
    }
  }

  handleError = async (request, callback, err) => {
    console.error('FEMA GO - Failed to autosave user input. Error:', err);
    goStore.store
      .dispatch(
        () =>
          new Promise((resolve, reject) => {
            debugBreakpoint(BREAKPOINTS.collaborationErrors);
            goStore.store.dispatch(
              DynamicComponentActions.handleSaveError({
                title: SAVE_ENTITY_NETWORK_ERROR_TITLE,
                text: SAVE_ENTITY_NETWORK_ERROR_MSG,
                message: err,
              })
            );
            resolve();
          })
      )
      .then(() => {
        this.entityQueue.delete(request.entityId);
        if (this.pendingQueue.has(request)) {
          this.pendingQueue.delete(request);
          this.next(request);
        }
        callback(request);
      });
  };

  dispatchEvents = async (request, response, callback) => {
    const concurrentEditVersionData = this.concurrentEditPayloadFromResponse(
      AUTOSAVE_FAILURE,
      response
    );

    goStore.store
      .dispatch(
        () =>
          new Promise((resolve, reject) => {
            let versionIdPath = request.entityTypeReduxPath
              ? _.join([request.entityTypeReduxPath, 'versionId'], '.')
              : this.getVersionIdPathByEntityType(request.entityType);
            goStore.store.dispatch(
              DynamicComponentActions.updateVersion(
                versionIdPath,
                concurrentEditVersionData.versionId,
                concurrentEditVersionData.modifiedBy,
                concurrentEditVersionData.isConcurrencyModalActive,
                concurrentEditVersionData.modifyDate
              )
            );
            resolve();
          })
      )
      .then(() => {
        this.entityQueue.delete(request.entityId);
        if (this.pendingQueue.has(request)) {
          this.pendingQueue.delete(request);
          this.next(request);
        }
        callback(request);
      });
  };

  concurrentEditPayloadFromResponse(actionType, response) {
    const responseType = _.get(response, 'type');
    const status = _.get(response, 'payload.status');
    const isConcurrencyModalActive = actionType === responseType && status === 409;
    let versionId, modifiedBy, modifyDate;
    if (isConcurrencyModalActive) {
      versionId = _.get(response, 'payload.response.versionId');
      modifiedBy = _.get(response, 'payload.response.modifiedBy');
      modifyDate = _.get(response, 'payload.response.modifyDate');
    } else {
      versionId = _.get(response, 'payload.versionId');
      modifiedBy = _.get(response, 'payload.modifiedBy');
      modifyDate = _.get(response, 'payload.modifyDate');
    }
    return {
      versionId: versionId,
      modifiedBy: modifiedBy,
      modifyDate: modifyDate,
      isConcurrencyModalActive: isConcurrencyModalActive,
    };
  }

  getVersionIdPathByEntityType(entityType) {
    switch (entityType) {
      case 'data':
        return 'data.versionId';
      default:
        return _.join(['context', entityType, 'versionId'], '.');
    }
  }

  next(event) {
    this.entityTypeMap.get(event.entityType).next(event);
  }
}

const ConcurrentEditProcessorObj = new ConcurrentEditProcessor();
export default ConcurrentEditProcessorObj;
